import { PanelPlacement } from '@ContractBuilder/hooks';
import { isNonNullish } from '@root/helpers';
import { isEqual } from 'lodash-es';
import { createWithEqualityFn } from 'zustand/traditional';

import type { PDFPreviewStore, TabRegistration } from '../types';

export const usePDFPreviewStore = createWithEqualityFn<PDFPreviewStore>(
  (set) => ({
    activeTabId: null,
    splitTabId: null,
    registeredTabs: new Map<string, TabRegistration>(),
    placement: PanelPlacement.Right,
    registerTab: (tab) =>
      set((state) => {
        const existingTab = state.registeredTabs.get(tab.id);

        if (
          existingTab &&
          existingTab.label === tab.label &&
          existingTab.documentUrl === tab.documentUrl &&
          existingTab.documentVersion === tab.documentVersion
        ) {
          return state;
        }

        const newTabs = new Map(state.registeredTabs);
        newTabs.set(tab.id, tab);

        // Auto-select first tab if none active
        const activeTabId = state.activeTabId ?? tab.id;
        return { registeredTabs: newTabs, activeTabId };
      }),
    setActiveTab: (id) => set(() => ({ activeTabId: id })),
    setSplitTab: (id) => set({ splitTabId: id }),
    toggleSplitMode: () =>
      set((state) => {
        if (state.splitTabId !== null) {
          return { splitTabId: null };
        }

        const tabs = Array.from(state.registeredTabs.values());
        const nonActiveTabId = tabs.find((t) => t.id !== state.activeTabId);

        if (nonActiveTabId) {
          return { splitTabId: nonActiveTabId.id, placement: PanelPlacement.Right };
        }

        return state;
      }),
    setPlacement: (placement) => set(() => ({ placement })),
    reset: () =>
      set({
        activeTabId: null,
        splitTabId: null,
        registeredTabs: new Map(),
        placement: PanelPlacement.Right,
      }),
  }),
  isEqual,
);

export const selectIsInSplitMode = (state: PDFPreviewStore) => state.splitTabId !== null;
export const selectCanEnableSplit = (state: PDFPreviewStore) => state.registeredTabs.size >= 2;
export const selectAvailableTabs = (state: PDFPreviewStore) => Array.from(state.registeredTabs.values());
export const selectActiveTab = (state: PDFPreviewStore) =>
  state.activeTabId ? state.registeredTabs.get(state.activeTabId) : undefined;
export const selectSplitTab = (state: PDFPreviewStore) =>
  state.splitTabId ? state.registeredTabs.get(state.splitTabId) : undefined;

export const selectDocumentUrls = (state: PDFPreviewStore) =>
  Array.from(state.registeredTabs.values())
    .map((tab) => tab.documentUrl)
    .filter(isNonNullish);
